import { useState, useEffect } from "react";
import {
  type Interface,
  type GenerateExecuteTemplateState,
} from "@zapier/client";
import {
  ModalDialogBody,
  ModalDialogHeader,
  ModalDialogRoot,
} from "@zapier/design-system";
import { useEmitInterfaceDuplicateModalEvent } from "@zapier/shared/src/events";

import { useDuplicateInterface } from "./useDuplicateInterface";
import LoadingCard from "./LoadingCard";
import CreatedResources from "./CreatedResources";

import styles from "./LoadingCard.module.css";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  interfaceObj: Interface | undefined;
  folderId?: string; // used for cache invalidation in mutations
}

const InterfaceDuplicateModalHeader = ({
  generateExecuteTemplateStatus,
}: {
  generateExecuteTemplateStatus?: GenerateExecuteTemplateState;
}) => {
  if (generateExecuteTemplateStatus?.status === "cloning") {
    return "Cloning your interface...";
  }

  if (generateExecuteTemplateStatus?.status === "creating") {
    return "Creating all assets...";
  }

  if (
    generateExecuteTemplateStatus?.status === "finished" &&
    generateExecuteTemplateStatus?.createdTemplate
  ) {
    return "Your project has been duplicated!";
  }

  return "";
};

const InterfaceDuplicateModalLoading = ({
  generateExecuteTemplateStatus,
}: {
  generateExecuteTemplateStatus?: GenerateExecuteTemplateState;
}) => {
  if (generateExecuteTemplateStatus?.status === "cloning") {
    return (
      <div className={styles.loadingCards}>
        <LoadingCard kind="interface" />
      </div>
    );
  }

  if (generateExecuteTemplateStatus?.status === "creating") {
    return (
      <div className={styles.loadingCards}>
        {generateExecuteTemplateStatus?.zsl?.chatbots?.map((_, index) => (
          <LoadingCard key={`chatbot-${index}`} kind="chatbot" />
        ))}
        {generateExecuteTemplateStatus?.zsl?.zaps?.map((_, index) => (
          <LoadingCard key={`zap-${index}`} kind="zap" />
        ))}
        {generateExecuteTemplateStatus?.zsl?.interfaces?.map((_, index) => (
          <LoadingCard key={`interface-${index}`} kind="interface" />
        ))}
        {generateExecuteTemplateStatus?.zsl?.tables?.map((_, index) => (
          <LoadingCard key={`table-${index}`} kind="table" />
        ))}
      </div>
    );
  }

  if (
    generateExecuteTemplateStatus?.status === "finished" &&
    generateExecuteTemplateStatus?.createdTemplate
  ) {
    return (
      <div className={styles.loadingCards}>
        <CreatedResources
          createdTemplate={generateExecuteTemplateStatus?.createdTemplate}
        />
      </div>
    );
  }

  return;
};

const InterfaceDuplicateModal = ({
  isOpen,
  onClose,
  interfaceObj,
  folderId,
}: Props) => {
  // State to track the current step and overall status
  const [generateExecuteTemplateStatus, setGenerateExecuteTemplateStatus] =
    useState<GenerateExecuteTemplateState | undefined>();

  const { duplicateInterface } = useDuplicateInterface({
    interfaceObject: interfaceObj,
    setGenerateExecuteTemplateStatus,
    onClose,
    folderId,
  });

  const { emit } = useEmitInterfaceDuplicateModalEvent();

  // Automatically execute duplication when modal opens and interfaceObj is available
  useEffect(() => {
    if (isOpen && interfaceObj && !generateExecuteTemplateStatus?.status) {
      duplicateInterface();
    }
  }, [
    isOpen,
    interfaceObj,
    duplicateInterface,
    generateExecuteTemplateStatus?.status,
  ]);

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      emit("cancel");
      onClose();
      setGenerateExecuteTemplateStatus(undefined);
    }
  };

  if (!interfaceObj) {
    return;
  }

  return (
    <ModalDialogRoot isOpen={isOpen} onOpenChange={handleOpenChange}>
      <ModalDialogHeader>
        <InterfaceDuplicateModalHeader
          generateExecuteTemplateStatus={generateExecuteTemplateStatus}
        />
      </ModalDialogHeader>
      <ModalDialogBody>
        <InterfaceDuplicateModalLoading
          generateExecuteTemplateStatus={generateExecuteTemplateStatus}
        />
      </ModalDialogBody>
    </ModalDialogRoot>
  );
};

export default InterfaceDuplicateModal;
